import type { AnimationEvent } from '@angular/animations';
import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import type { Portal } from '@angular/cdk/portal';
import type { EventEmitter } from '@angular/core';
import type { CalloutColor } from './callout-color';
import type { CalloutPlacement } from './callout-placement';
import type { CalloutSize } from './callout-size';

export interface CalloutTriggerOpenInterface {
  isPinnedOnClick?: boolean;
}

export interface CalloutTriggerComponentInterface {
  // Outputs
  trigger: EventEmitter<MouseEvent>;
  open: EventEmitter<CalloutTriggerOpenInterface>;
  close: EventEmitter<void>;
}

export interface CalloutContentComponentInterface {
  // Outputs
  bodyClick: EventEmitter<void>;
  bodyMouseenter: EventEmitter<void>;
  bodyMouseleave: EventEmitter<void>;
  close: EventEmitter<void>;
}

export interface CalloutComponentInterface {
  // Inputs
  color: CalloutColor;
  edgeToEdge: boolean;
  hideCloseButton: boolean;
  hover: boolean;
  placement: CalloutPlacement;
  size?: CalloutSize;
  mobileCloseButton: boolean;
  mobileFullscreen: boolean;
  mobileLongPress: boolean;
  mobileForceClick: boolean;
  mobileHeaderTitle: string;

  // Outputs
  calloutClose: EventEmitter<void>;
  calloutOpen: EventEmitter<void>;

  triggerComponent: CalloutTriggerComponentInterface;
  contentComponent: CalloutContentComponentInterface;
  portal: Portal<any>;

  animationDone(event: AnimationEvent): void;

  open(): void;

  close(): void;

  toggle(): void;

  updatePosition(): void;

  togglePageScrollState(isCalloutOpen: boolean): void;
}

/**
 * Keep in sync with `$navigation-height-tablet` in
 * `styles/settings/_variables.scss` (with 10px buffer).
 */
export const FIXED_NAVIGATION_VIEWPORT_MARGIN = 60;

/**
 * Extra configuration for callout viewport fit behavior.
 * If not provided, default options will be used.
 */
export interface ViewportFitOptions {
  /**
   * Viewport margin to account for fixed elements like navigation bars.
   * Defaults to the fixed primary navigation's clearance; pass `0` to opt out.
   */
  viewportMargin?: number;
}

/**
 * Configuration for ensuring the callout fits within the viewport
 * and can auto-switch its position.
 */
export interface CalloutViewportFit {
  /**
   * Whether to enable viewport fit logic.
   * Default: true
   */
  enabled: boolean;

  /**
   * Extra options for viewport fit behavior.
   * If not provided, default auto positioning options will be used.
   */
  options?: ViewportFitOptions;
}

/**
 * For callouts owned by the navigation: `viewportMargin: 0` because the
 * margin insets all four viewport edges, which would push a full-width
 * mega-menu away from the edges it is meant to span.
 */
export const NAVIGATION_OWNED_VIEWPORT_FIT: CalloutViewportFit = {
  enabled: false,
};

/**
 * The current position of a callout.
 * Different to CalloutPlacement, which is the intended default position
 */
export type CalloutPosition = Pick<
  ConnectionPositionPair,
  'overlayX' | 'overlayY' | 'offsetX' | 'offsetY'
> & {
  anchor: 'overlayX' | 'overlayY';
};
